import * as styles from '../FirstResponder.module.css';
import { LivePagination } from './liveViewModel';

/*
 * Pagination for the LIVE preview. Mirrors the BEHAVIOR contract of the shared
 * src/components/Pagination.tsx (first/previous/next/last, boundary
 * disabling, "Page X of Y", accessible labels) but is styled with the
 * prototype's module CSS — the shared component's Stitches/FontAwesome
 * rendering cannot preserve the accepted HTML look, so its behavior is
 * reused rather than inventing conflicting pagination logic.
 *
 * Hidden entirely for zero or one page (never "Page 1 of 0").
 */

type Props = {
  pagination: LivePagination;
  onPageChange: (page: number) => void;
  itemsLabel: string; // e.g. "jobs", "workers" — used in the range text
};

const PreviewPagination = ({ pagination, onPageChange, itemsLabel }: Props) => {
  const { total, page, perPage, totalPages } = pagination;
  if (totalPages <= 1) return null;

  const first = (page - 1) * perPage + 1;
  const last = Math.min(page * perPage, total);

  const btn = `${styles.btn} ${styles.btnOutline} ${styles.btnSm}`;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '12px 20px',
        flexWrap: 'wrap',
      }}
      data-testid={`flp-pagination-${itemsLabel}`}
    >
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          type="button"
          className={btn}
          aria-label="First page"
          disabled={page === 1}
          onClick={() => onPageChange(1)}
        >
          «
        </button>
        <button
          type="button"
          className={btn}
          aria-label="Previous page"
          disabled={page === 1}
          onClick={() => onPageChange(Math.max(page - 1, 1))}
        >
          ‹
        </button>
      </div>
      <span
        className={`${styles.muted} ${styles.small}`}
        data-testid={`flp-page-info-${itemsLabel}`}
      >
        Page {page} of {totalPages}
        <span>
          {' '}
          · {first}–{last} of {total} {itemsLabel}
        </span>
      </span>
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          type="button"
          className={btn}
          aria-label="Next page"
          disabled={page === totalPages}
          onClick={() => onPageChange(Math.min(page + 1, totalPages))}
        >
          ›
        </button>
        <button
          type="button"
          className={btn}
          aria-label="Last page"
          disabled={page === totalPages}
          onClick={() => onPageChange(totalPages)}
        >
          »
        </button>
      </div>
    </div>
  );
};

export default PreviewPagination;
